import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { createProject, listProjects } from '@/data/projects'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { percent } from '@/lib/reportMath'
import { amountToNgwee, projectFormSchema } from './schemas'

const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

export function ProjectsPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const router = useRouter()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['projects', deptId], queryFn: () => listProjects(deptId) })

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [target, setTarget] = useState('')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')
  const [wide, setWide] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const busy = useRef(false)
  const clientRef = useRef(newRef())
  const createM = useMutation({ mutationFn: (v: { wide: boolean; input: Parameters<typeof createProject>[1]; ref: string }) => createProject(v.wide ? null : deptId, v.input, v.ref) })

  const openForm = () => {
    setName(''); setDesc(''); setTarget(''); setStartsOn(''); setEndsOn(''); setWide(false); setErrors({}); setFormError(''); clientRef.current = newRef(); setOpen(true)
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setFormError('')
    const parsed = projectFormSchema.safeParse({ name, description: desc, target, startsOn, endsOn })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
      setErrors(errs)
      return
    }
    setErrors({})
    const v = parsed.data
    busy.current = true
    try {
      const id = await createM.mutateAsync({
        wide,
        ref: clientRef.current,
        input: { name: v.name, description: v.description, targetNgwee: v.target.trim() === '' ? null : amountToNgwee(v.target), startsOn: v.startsOn || null, endsOn: v.endsOn || null },
      })
      await qc.invalidateQueries({ queryKey: ['projects', deptId] })
      setOpen(false)
      await router.navigate({ to: '/d/$deptId/projects/$projectId', params: { deptId, projectId: id } })
    } catch (err) {
      setFormError(errText(err))
    } finally {
      busy.current = false
    }
  }

  const projects = q.data ?? []
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Projects</h2>
        <p className="mt-1 text-muted">One-off giving for something specific, like uniforms, a trip or an event.</p>
      </div>
      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Projects you can give to</h3>
          {canWrite && <Button size="lg" onClick={openForm}>Start a project</Button>}
        </CardHeader>
        <CardBody>
          {q.isPending ? (
            <SkeletonRows rows={4} />
          ) : q.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(q.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void q.refetch()}>Try again</Button>
            </div>
          ) : projects.length === 0 ? (
            <EmptyState title="No projects yet" description={canWrite ? 'Start one when the department is raising money for something.' : 'Nothing has been started.'} />
          ) : (
            <ul className="divide-y divide-line">
              {projects.map((p) => (
                <li key={p.id}>
                  <Link to="/d/$deptId/projects/$projectId" params={{ deptId, projectId: p.id }} className="block py-3 hover:bg-page">
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block font-medium text-ink">{p.name}</span>
                        {p.description && <span className="block text-body text-muted">{p.description}</span>}
                        <span className="mt-1 flex flex-wrap gap-1">
                          {p.departmentId === null && <Badge tone="info">Whole church</Badge>}
                          {p.closedAt && <Badge tone="neutral">Closed</Badge>}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block font-medium text-ink">{formatKwacha(p.raisedNgwee)}</span>
                        {p.targetNgwee !== null && <span className="block text-micro text-muted">of {formatKwacha(p.targetNgwee)}</span>}
                      </span>
                    </span>
                    {p.targetNgwee !== null && (
                      <span className="mt-2 block h-2 max-w-sm overflow-hidden rounded-full bg-neutral-tint" role="img" aria-label={`${percent(p.raisedNgwee, p.targetNgwee)}% of the target`}>
                        <span className="block h-full bg-brand" style={{ width: `${percent(p.raisedNgwee, p.targetNgwee)}%` }} />
                      </span>
                    )}
                    {(p.startsOn || p.endsOn) && <span className="mt-1 block text-micro text-muted">{p.startsOn ? `From ${formatDay(p.startsOn)}` : ''}{p.startsOn && p.endsOn ? ' ' : ''}{p.endsOn ? `until ${formatDay(p.endsOn)}` : ''}</span>}
                  </Link>
                  <OwnerDeleteButton table="project" id={p.id} what="project" wrap="flex flex-wrap gap-2 pb-3" />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={open} onClose={() => setOpen(false)} title="Start a project">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Name" placeholder="Camp uniforms" value={name} onChange={(e) => setName(e.target.value)} error={errors.name} />
          <Textarea label="What is it for? (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} error={errors.description} rows={2} />
          <Input label="Target in Kwacha (optional)" inputMode="decimal" placeholder="5000" value={target} onChange={(e) => setTarget(e.target.value)} error={errors.target} />
          <Input label="Starts (optional)" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} error={errors.startsOn} />
          <Input label="Ends (optional)" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} error={errors.endsOn} />
          {profile.isAdmin && (
            <label className="flex min-h-[44px] items-center gap-3 text-body font-medium text-neutral">
              <input type="checkbox" className="h-5 w-5" checked={wide} onChange={(e) => setWide(e.target.checked)} />
              Open to every department (whole church)
            </label>
          )}
          {formError && <p role="alert" className="text-body text-bad">{formError}</p>}
          <Button type="submit" size="lg" isLoading={createM.isPending}>{createM.isPending ? 'Saving...' : 'Start project'}</Button>
        </form>
      </Sheet>
    </div>
  )
}
